import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { writeContract, waitForTransactionReceipt } from '@wagmi/core';
import postsContractABI from '../../contracts/tgetherPosts.json';
import * as config from '../../config.tsx';

const postsContractAddress = process.env.REACT_APP_POSTS_CONTRACT_ADDRESS as `0x${string}`;

interface MintPostModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
}

function MintPostModal({ show, setShow }: MintPostModalProps) {
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 4;
    const [formData, setFormData] = useState({
        content: '',
        title: '',
        authorName: '',
        description: ''
    });
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [transactionAlert, setTransactionAlert] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (!show) {
            // Reset form state when modal is closed
            setFormData({
                content: '',
                title: '',
                authorName: '',
                description: ''
            });
            setCurrentStep(1);
            setErrorMessage('');
            setTransactionAlert('');
        }
    }, [show]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleNext = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrev = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleClose = () => {
        setShow(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setLoading(true);
        setErrorMessage('');
        setTransactionAlert('Transaction is being processed. Please wait...');

        try {
            const tx = await writeContract(config.config, {
                address: postsContractAddress,
                abi: postsContractABI.abi,
                functionName: 'mintPost',
                args: [formData.content, formData.title, formData.authorName, formData.description],
            });


            const receipt = await waitForTransactionReceipt(config.config,{
              hash: tx, // Access the transaction hash
            });
            console.log(receipt)

            const hexValue = receipt.logs[0].topics[3]; // example: "0x0000000000000000000000000000000000000000000000000000000000000006"
            const numberValue = Number(hexValue); // Converts hex to integer

            setTransactionAlert(''); // Clear the alert
            setLoading(false);
            navigate(`/search-results?type=Post&value=${encodeURIComponent(numberValue)}`);
            setShow(false);
        } catch (error) {
            console.error("Error minting post:", error);
            setErrorMessage('There was an error minting the post. Please try again.');
            setLoading(false);
            setTransactionAlert('');
        }
    };

    const renderTooltip = (text: string) => (
        <Tooltip id="button-tooltip">{text}</Tooltip>
    );

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <Form.Group>
                        <Form.Label>
                            Title{' '}
                            <OverlayTrigger
                                placement="right"
                                overlay={renderTooltip("Enter the title of your post. Make it descriptive and catchy.")}>
                                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                                    <i className="bi bi-patch-question-fill"></i>
                                </Button>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>
                );
            case 2:
                return (
                    <Form.Group>
                        <Form.Label>
                            Content{' '}
                            <OverlayTrigger
                                placement="right"
                                overlay={renderTooltip("Enter the content of your post. This will be visible to others.")}>
                                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                                    <i className="bi bi-patch-question-fill"></i>
                                </Button>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            name="content"
                            value={formData.content}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>
                );
            case 3:
                return (
                    <Form.Group>
                        <Form.Label>
                            Author Name{' '}
                            <OverlayTrigger
                                placement="right"
                                overlay={renderTooltip("Enter your name or a pseudonym that will be associated with the post.")}>
                                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                                    <i className="bi bi-patch-question-fill"></i>
                                </Button>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="authorName"
                            value={formData.authorName}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>
                );
            case 4:
                return (
                    <Form.Group>
                        <Form.Label>
                            Description{' '}
                            <OverlayTrigger
                                placement="right"
                                overlay={renderTooltip("Provide a brief description or summary of your post.")}>
                                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                                    <i className="bi bi-patch-question-fill"></i>
                                </Button>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Mint a New Post</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {transactionAlert && <Alert variant="info">{transactionAlert}</Alert>}
                    {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                    <Form onSubmit={handleSubmit}>
                        {renderStep()}
                        <ProgressBar now={(currentStep / totalSteps) * 100} className="my-3" />
                        <div className="d-flex justify-content-between">
                            <Button variant="outline-secondary" onClick={handlePrev} disabled={currentStep === 1}>
                                Previous
                            </Button>
                            {currentStep < totalSteps ? (
                                <Button variant="info" onClick={handleNext}>
                                    Next
                                </Button>
                            ) : (
                                <Button variant="primary" type="submit" disabled={loading}>
                                    {loading ? 'Minting...' : 'Submit Post'}
                                </Button>
                            )}
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default MintPostModal;
